import { useState, useEffect } from 'react'

import { AppIcon  } from '@/shared/ui/icons'

import { getAllStates, getStateCities } from 'easy-location-br'
import _ from 'lodash'
import { useFormContext, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { pt } from 'yup-locale-pt'

yup.setLocale(pt)

import { Button } from '@/shared/ui'
import {
  FormControl,
  FormGroup,
  InputGroup,
  PostalCodeField,
  SelectField,
  SwitchField
} from '@/shared/ui/Form'
import getRules from '@/shared/ui/Form/lib/rules'

import { getAddressByZipCode } from './services/getAddressByZipCode'
import { getValidationSchema } from './validationSchema'

// `FormGroup` repassa ao controle props do wrapper legado (`solid`, `vertical`,
// `required`) que o `SelectField` não conhece e que vazariam para o DOM. Este
// adaptador fica na feature, e não no design system: é dívida do wrapper antigo,
// não contrato do componente novo.
const SelectFieldControl = ({ solid, vertical, required, ...rest }) => (
  <SelectField {...rest} />
)

export const DEFAULT_NAMES = {
  zipCode: 'zip_code',
  street: 'street',
  number: 'number',
  state: 'state',
  city: 'city',
  district: 'district',
  complement: 'complement',
  city_cod: 'city_cod'
}

// const isValidUSZipCode = zipCode =>
//   /(^\d{5}$)|(^\d{5}-\d{4}$)/.test((zipCode || '').trim())

const AddressFields = ({
  namePrefix: _namePrefix,
  names: _names,
  solid = false,
  horizontal = false,
  showSaveAsDefault = false,
  nestedFieldsSlot,
  hideFieldsUntilZipCode = false,
  country,
  required = true,
  language
}) => {
  const [zipCodeInfoLoadingStatus, setZipCodeInfoLoadingStatus] =
    useState('initial')
  const [zipCodeTouched, setZipCodeTouched] = useState(false)
  const { setValue, getValues } = useFormContext()
  const isBrazil = !country || country == 'BRA'
  const isPortuguese = !language || language == 'pt'
  const names = { ...DEFAULT_NAMES, ..._names }
  const namePrefix = _namePrefix ? `${_namePrefix}.` : ''
  const watchZipCode = useWatch({
    name: `${namePrefix}${names.zipCode}`,
    defaultValue: getValues(`${namePrefix}${names.zipCode}`)
  })
  const watchState = useWatch({ name: `${namePrefix}${names.state}` })
  const zipCode = watchZipCode?.replace(/\D/g, '')
  const zipCodeHasValidLength = zipCode?.length === 8
  const vertical = !horizontal
  const blockEditing =
    isBrazil &&
    (!['success', 'unreachable', 'bypass', 'initial'].includes(
      zipCodeInfoLoadingStatus
    ) ||
      !zipCodeHasValidLength)
  const disableAddressFields = false
  // const disableAddressFields = zipCodeInfoLoadingStatus == 'success'

  const loadAddress = async () => {
    if (!zipCodeHasValidLength || !isBrazil) return

    setZipCodeInfoLoadingStatus('loading')

    try {
      const { street, district, city, cityCod, uf, error } =
        await getAddressByZipCode({ zipCode })

      if (error) {
        setValue(`${namePrefix}${names.street}`, '')
        setValue(`${namePrefix}${names.district}`, '')
        setValue(`${namePrefix}${names.state}`, '')
        setValue(`${namePrefix}${names.city}`, '')
        setValue(`${namePrefix}${names.city_cod}`, '')

        setZipCodeInfoLoadingStatus('error')
      } else {
        if (street) setValue(`${namePrefix}${names.street}`, street)
        if (district) setValue(`${namePrefix}${names.district}`, district)
        if (city) setValue(`${namePrefix}${names.city}`, city)
        if (cityCod) setValue(`${namePrefix}${names.city_cod}`, cityCod)
        if (uf)
          setValue(`${namePrefix}${names.state}`, uf, { shouldValidate: true })

        setZipCodeInfoLoadingStatus('success')
      }
    } catch (error) {
      setZipCodeInfoLoadingStatus('unreachable')
    }
  }

  useEffect(() => {
    const zipCodeValue = getValues(`${namePrefix}${names.zipCode}`)
    if (zipCodeValue && !watchZipCode)
      setValue(`${namePrefix}${names.zipCode}`, zipCodeValue)

    if (zipCodeTouched) loadAddress()
  }, [watchZipCode])

  return (
    <>
      <FormControl name={`${namePrefix}${names.city_cod}`} hidden />
      <FormGroup
        name={`${namePrefix}${names.zipCode}`}
        label={isPortuguese ? 'CEP' : 'Zip Code'}
        vertical={vertical}
        solid={solid}
        required={required}
      >
        <div className="flex">
          {isBrazil ? (
            <InputGroup
              className={`${nestedFieldsSlot ? 'mr-2' : ''}`}
              solid={solid}
            >
              <PostalCodeField
                name={`${namePrefix}${names.zipCode}`}
                rules={getRules({ required })}
                onChange={() => !zipCodeTouched && setZipCodeTouched(true)}
              />
              <Button
                type="button"
                variant={solid ? 'light' : 'secondary'}
                icon
                size="sm"
                title="Atualizar CEP"
                disabled={zipCodeInfoLoadingStatus == 'loading'}
                leadingIcon={
                  <AppIcon
                    name="arrows-rotate"
                    spin={zipCodeInfoLoadingStatus == 'loading'}
                    className={
                      zipCodeInfoLoadingStatus == 'loading' ? 'text-brand' : ''
                    }
                  />
                }
                className="shrink-0 rounded-none border-0"
                onClick={loadAddress}
                aria-label="Atualizar CEP"
              />
            </InputGroup>
          ) : (
            <FormControl
              name={`${namePrefix}${names.zipCode}`}
              solid={solid}
              required={required}
            />
          )}
          {nestedFieldsSlot}
        </div>
      </FormGroup>
      {(!isBrazil || !hideFieldsUntilZipCode || zipCodeHasValidLength) && (
        <div
          className={`relative ${blockEditing ? 'cursor-default' : ''}`}
        >
          <div className="grid grid-cols-12 gap-x-4">
            <div className="col-span-12">
              <FormGroup
                name={`${namePrefix}${names.street}`}
                label={isPortuguese ? 'Rua/Av/Logradouro' : 'Address'}
                control={FormControl}
                disabled={disableAddressFields}
                vertical={vertical}
                solid={solid}
                required={required}
              />
            </div>
            <div className={vertical ? 'col-span-12 md:col-span-4 xl:col-span-3' : 'col-span-12'}>
              <FormGroup
                name={`${namePrefix}${names.number}`}
                label={isPortuguese ? 'Nº' : 'Number'}
                control={FormControl}
                vertical={vertical}
                solid={solid}
                required={required}
              />
            </div>
            <div className={vertical ? 'col-span-12 md:col-span-8 xl:col-span-6' : 'col-span-12'}>
              <FormGroup
                name={`${namePrefix}${names.district}`}
                label={isPortuguese ? 'Bairro' : 'Neighborhood '}
                control={FormControl}
                disabled={disableAddressFields}
                vertical={vertical}
                solid={solid}
                required={required}
              />
            </div>
            <div className={vertical ? 'col-span-12 md:col-span-5 lg:col-span-3 xl:col-span-3' : 'col-span-12'}>
              <FormGroup
                name={`${namePrefix}${names.state}`}
                label={isPortuguese ? 'UF' : 'State'}
                control={isBrazil ? SelectFieldControl : FormControl}
                options={getAllStates().map(s => ({
                  label: s.id,
                  value: s.id
                }))}
                disabled={disableAddressFields}
                vertical={vertical}
                solid={solid}
                required={required}
              />
            </div>
            <div className={vertical ? 'col-span-12 md:col-span-7 lg:col-span-9 xl:col-span-6' : 'col-span-12'}>
              <FormGroup
                name={`${namePrefix}${names.city}`}
                label={isPortuguese ? 'Cidade' : 'City'}
                control={isBrazil ? SelectFieldControl : FormControl}
                options={getStateCities(watchState).map(s => ({
                  label: s.name,
                  value: s.name
                }))}
                disabled={disableAddressFields}
                vertical={vertical}
                solid={solid}
                required={required}
              />
            </div>
            <div className={vertical ? 'col-span-12 xl:col-span-6' : 'col-span-12'}>
              <FormGroup
                name={`${namePrefix}${names.complement}`}
                label={isPortuguese ? 'Complemento' : 'Line 1'}
                control={FormControl}
                solid={solid}
                vertical={vertical}
              />
            </div>
            {showSaveAsDefault && (
              <div className="col-span-12">
                <SwitchField
                  name={`${namePrefix}default`}
                  // Nasce ligado, como no wrapper legado: sem isto o endereço
                  // deixa de ser salvo como padrão e o payload muda em silêncio.
                  defaultValue
                  label="Salvar como principal?"
                  description="Quando você precisar efetuar novas compras, este endereço será usado"
                />
              </div>
            )}
          </div>
          {isBrazil && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/50">
              {!zipCodeHasValidLength && (
                <span className="inline-flex cursor-default items-center rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700">
                  Primeiro insira o CEP
                </span>
              )}
              {zipCodeInfoLoadingStatus == 'loading' && (
                <AppIcon name="arrows-rotate" spin className="text-primary" />
              )}
              {zipCodeHasValidLength && zipCodeInfoLoadingStatus == 'error' && (
                <span className="bg-danger-light px-5 py-4 rounded-lg flex flex-col items-center">
                  <span className="text-danger font-bold text-center mb-4">
                    O <b>Correios</b> não retornou nenhum endereço válido para
                    este CEP, verifique os dígitos e tente novamente ou continue
                    digitando manualmente
                  </span>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => setZipCodeInfoLoadingStatus('bypass')}
                  >
                    Digitar manualmente
                  </Button>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}

AddressFields.getValidationSchema = getValidationSchema

export { AddressFields }
export default AddressFields
export { getValidationSchema as getAddressFieldsValidationSchema }
